/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { Suspense } from 'react';

import type { Environment, PreloadedQuery } from 'react-relay';
import {
	type Route,
	type RouteContext,
	type RouteResource,
	type RouteResources,
	withRouter,
} from 'react-resource-router';

import { cssMap, jsx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import UFOSegment from '@atlaskit/react-ufo/ufo-segment';
import Spinner from '@atlaskit/spinner/spinner';
import type { hubBootstrapWrapperQuery } from '@atlassian/company-hub-common/hub-bootstrap/hubBootstrapWrapperQuery';
import { mockHubRouteProps } from '@atlassian/company-hub-common/smart-card-mocks';
import type { HubAppProps } from '@atlassian/company-hub-common/types';
import { GenericErrorPage } from '@atlassian/company-hub-common/ui/generic-error-page';
import { HubBootstrapWrapper } from '@atlassian/company-hub-common/ui/hub-bootstrap';
import { HubErrorBoundary } from '@atlassian/company-hub-common/ui/hub-error-boundary';
import { HubAppShell } from '@atlassian/hub-app-shell/main';

import {
	createNavigationBarResource,
	hubBootstrapResource,
	hubPageResource,
	rovoWidgetResource,
} from '../services/resources';
import { useRelayResource } from '../services/resources/utils';
import { HubApp } from '../ui/hub';
import type { hubQuery } from '../ui/hub/__generated__/hubQuery.graphql';
import { getContentWidth, type GridAppearanceTypes, withBannerBackgroundColor } from '../utils';
import {
	HUB_CUSTOM_PAGE_ROUTE_NAME,
	HUB_CUSTOM_PAGE_ROUTE_PATH,
	HUB_PORTAL_PAGE_ROUTE_NAME,
	HUB_PORTAL_PAGE_ROUTE_PATH,
	HUB_PREVIEW_ROUTE_NAME,
	HUB_PREVIEW_ROUTE_PATH,
	HUB_ROUTE_NAME,
	HUB_ROUTE_PATH,
	HUB_ROUTE_PATH_OPTIONAL_SLASH,
} from './constants';

// Override ResourceStoreContext type
declare module 'react-resource-router' {
	interface ResourceStoreContext {
		cloudId: string;
		helpCenterAri: string;
		/**
		 * Whether the surface is a JSM Help Hub rather than a CSM support site, which registers
		 * the same routes. Set by the host because `HelpCenter.type` arrives in the hub query
		 * response — too late to gate what that query asks for.
		 */
		isHelpHub?: boolean;
		relayEnvironment?: Environment;
		shouldAddEdgeFallbackToUnidentified?: boolean;
	}
}

const styles = cssMap({
	spinnerContainer: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		height: '100vh',
	},
	root: {
		display: 'flex',
		flexDirection: 'column',
		minHeight: '100%',
		margin: '0 auto',
		width: '100%',
	},
});

export type HubRoute = Route & {
	resources: RouteResources;
};

export const hubHomepageRoute: {
	component: (props: HubAppProps) => JSX.Element | null;
	exact: boolean;
	name: string;
	path: string;
	resources: (
		| RouteResource<PreloadedQuery<hubBootstrapWrapperQuery, Record<string, unknown>>>
		| RouteResource<PreloadedQuery<hubQuery, Record<string, unknown>>>
	)[];
} = {
	component: HubApp,
	exact: true,
	name: HUB_ROUTE_NAME,
	resources: [hubBootstrapResource, hubPageResource],
	path: fg('enable-ssr-for-hubs') ? HUB_ROUTE_PATH_OPTIONAL_SLASH : HUB_ROUTE_PATH,
};

export const hubCustomPageRoute: {
	component: (props: HubAppProps) => JSX.Element | null;
	exact: boolean;
	name: string;
	path: string;
	resources: (
		| RouteResource<PreloadedQuery<hubBootstrapWrapperQuery, Record<string, unknown>>>
		| RouteResource<PreloadedQuery<hubQuery, Record<string, unknown>>>
	)[];
} = {
	component: HubApp,
	exact: true,
	name: HUB_CUSTOM_PAGE_ROUTE_NAME,
	resources: [hubBootstrapResource, hubPageResource],
	path: HUB_CUSTOM_PAGE_ROUTE_PATH,
};

/**
 * Whether to preload `rovoWidgetProviderQuery` as a route resource.
 *
 * Skipped once `hubs_rovo_config_from_kosh` is on, because `hubQuery` then carries `rovoConfig`
 * (served by help-kosh from convo-ai's licence-independent resolver) and is the only source of
 * truth. The callout banner skips its own lazy load off the same gate, so with the gate on this
 * query does not run at all — including when help-kosh resolves the field to null, which is its
 * answer rather than a reason to ask again.
 */
const shouldPreloadRovoWidgetQuery = (): boolean =>
	fg('hub_preload_rovo_widget_query') && !fg('hubs_rovo_config_from_kosh');

const shouldPreloadNavigationBarQuery = (hubAppProps: HubAppProps): boolean =>
	fg('hub_preload_navigation_bar_query') &&
	hubAppProps.featureFlags?.hub_component_upgrade === true;

const getHubRouteResources = (
	resources: RouteResources,
	hubAppProps: HubAppProps,
): RouteResources => [
	...resources,
	...(shouldPreloadNavigationBarQuery(hubAppProps)
		? [createNavigationBarResource(hubAppProps)]
		: []),
	...(shouldPreloadRovoWidgetQuery() ? [rovoWidgetResource] : []),
];

export const createHubHomepageRoute = (hubAppProps: HubAppProps): HubRoute => {
	const HubAppComponent = () => {
		return <HubApp {...hubAppProps} showPageNameAsHubDocumentTitle />;
	};
	return {
		...hubHomepageRoute,
		component: HubAppComponent,
		resources: getHubRouteResources(hubHomepageRoute.resources, hubAppProps),
	};
};

export const createHubPreviewRoute = (): HubRoute => {
	const hubRoute = createHubHomepageRoute(mockHubRouteProps);

	return {
		...hubRoute,
		path: HUB_PREVIEW_ROUTE_PATH,
		name: HUB_PREVIEW_ROUTE_NAME,
	};
};

export const createHubCustomPageRoute = (hubAppProps: HubAppProps): HubRoute => {
	const HubCustomPageComponent = ({ match }: RouteContext) => {
		return (
			<HubApp
				{...hubAppProps}
				customPageBreadcrumbPath={match.url}
				showPageNameAsHubDocumentTitle
				showCustomPageBreadcrumbs
			/>
		);
	};
	return {
		...hubCustomPageRoute,
		component: HubCustomPageComponent,
		resources: getHubRouteResources(hubCustomPageRoute.resources, hubAppProps),
	};
};

export const hubPortalPageRoute: {
	component: (props: HubAppProps) => JSX.Element | null;
	exact: boolean;
	name: string;
	path: string;
	resources: (
		| RouteResource<PreloadedQuery<hubBootstrapWrapperQuery, Record<string, unknown>>>
		| RouteResource<PreloadedQuery<hubQuery, Record<string, unknown>>>
	)[];
} = {
	component: HubApp,
	exact: true,
	name: HUB_PORTAL_PAGE_ROUTE_NAME,
	resources: [hubBootstrapResource, hubPageResource],
	path: HUB_PORTAL_PAGE_ROUTE_PATH,
};

export const createHubPortalPageRoute = (hubAppProps: HubAppProps): HubRoute => {
	const HubPortalPageComponent = ({ match }: RouteContext) => {
		return (
			<HubApp
				{...hubAppProps}
				portalBreadcrumbPath={match.url}
				showPageNameAsHubDocumentTitle
				showPortalBreadcrumbs
			/>
		);
	};
	return {
		...hubPortalPageRoute,
		component: HubPortalPageComponent,
		resources: getHubRouteResources(hubPortalPageRoute.resources, hubAppProps),
	};
};

export const createRouteWithHubShell = (
	hubAppProps: HubAppProps,
	InnerComponent: React.ComponentType,
	path: string,
	name: string,
	resources: RouteResources,
	componentWidth: GridAppearanceTypes,
	/**
	 * For routes that need the shell's providers (it mounts `HubTypographyScope`)
	 * but already ship their own header.
	 */
	options?: { hideNavbar?: boolean },
): HubRoute => {
	const Component = () => {
		const { queryReference: hubBootstrapQueryReference } = useRelayResource(hubBootstrapResource);

		if (!hubBootstrapQueryReference) {
			return null;
		}

		const InnerComponentWithRouter = withRouter(InnerComponent);
		const ComponentWithBannerBackgroundColor = withBannerBackgroundColor(InnerComponentWithRouter);

		const cloudId = hubBootstrapQueryReference.variables.cloudId;

		return (
			<UFOSegment name="hub-shell">
				<HubErrorBoundary
					shouldForceError
					id="route-with-hub-shell"
					packageName="@atlassian/hub"
					fallback={<GenericErrorPage />}
					operationNames={['hubBootstrapWrapperQuery']}
				>
					<Suspense
						fallback={
							<UFOLoadHold name="hub-shell-loader">
								<div css={styles.spinnerContainer}>
									<Spinner size={'large'} />
								</div>
							</UFOLoadHold>
						}
					>
						<HubBootstrapWrapper
							queryReference={hubBootstrapQueryReference}
							{...hubAppProps}
							cloudId={cloudId}
						>
							<HubAppShell hideNavbar={options?.hideNavbar}>
								{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values*/}
								<div css={styles.root} style={{ maxWidth: getContentWidth(componentWidth) }}>
									<ComponentWithBannerBackgroundColor />
								</div>
							</HubAppShell>
						</HubBootstrapWrapper>
					</Suspense>
				</HubErrorBoundary>
			</UFOSegment>
		);
	};

	return {
		component: Component,
		exact: true,
		name,
		path,
		resources: [hubBootstrapResource, ...resources],
	};
};
